import { ReactNode, useCallback, useRef, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';

import Button from "@ergeon/core-components/exports/Button.ts";

import UiStarIcon from '@/icons/UiStarIcon';
import { useMeetingData, useUserData } from '../queries/MeetingQuery/context';
import useFeedbackMutation from '../mutations/FeedbackMutation/useFeedbackMutation';
import ButtonReview from './ButtonReview';
import { EVENTS, gtag } from '@/lib/gtag';

interface DialogFeedbackProps {
  children: ReactNode;
  onEndCall(): void;
}

const RATING_WORDS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'OK',
  4: 'Good',
  5: 'Excellent',
};

export default function DialogFeedback({ children, onEndCall }: DialogFeedbackProps) {
  const [selectedStar, setSelectedStar] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [hasSent, setHasSent] = useState(false);
  const alreadyPosted = useRef(false);
  const dismissRef = useRef<HTMLElement>(null);

  const { mutate } = useFeedbackMutation();
  const data = useMeetingData();
  const { isHost } = useUserData();

  const reviewUrls = data.meeting_data.review_urls || [];

  const post = useCallback((nps: number) => {
    if (alreadyPosted.current || nps === 0) {
      return;
    }
    alreadyPosted.current = true;

    const { meeting_urn: urn, user_id } = data.meeting_data;
    mutate({ urn, user: Number(user_id), nps });
    gtag(EVENTS.ADD_RATING, { nps });
  }, [data, mutate]);

  const handleSend = useCallback(() => {
    post(selectedStar);
    setHasSent(true);
  }, [post, selectedStar]);

  const handleOnOpenChange = useCallback((open: boolean) => {
    if (open) {
      return;
    }
    // Someone who picked a star and then swatted the dialog away meant that
    // star: the extra tap on Send buys the chance to correct a slip, it does
    // not make the rating conditional on a second gesture.
    post(selectedStar);
    onEndCall();
  }, [onEndCall, post, selectedStar]);

  // Radix parks focus inside the radio group, where one press of Space commits
  // the leftmost star. Ending a call is the last thing a customer does before
  // putting the phone away, so that keystroke is entirely plausible — and it
  // writes a one-star rating that cannot be taken back.
  const keepFocusOffTheStars = useCallback((event: Event) => {
    event.preventDefault();
    dismissRef.current?.focus();
  }, []);

  const title = isHost
    ? 'Call ended'
    : hasSent ? 'Thank you for your feedback!' : 'How was your live video estimate?';

  return (
    <Dialog.Root onOpenChange={handleOnOpenChange}>
      <Dialog.Trigger asChild>
        {children}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content
          className="dialog-content fixed left-1/2 top-1/2 max-h-[85vh] w-11/12 max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-2xl focus:outline-none dark:bg-gray-800 dark:text-gray-100"
          onOpenAutoFocus={keepFocusOffTheStars}>
          <Dialog.Title className="mb-1 text-center font-heading text-base font-bold leading-6">
            {title}
          </Dialog.Title>

          {/* The account manager used to get the same star row on their own
              call, and took it: staff ratings were 39% of everything collected
              and 98% of them were five stars. */}
          {!isHost && !hasSent && (
            <>
              <div className="mt-4 flex items-center justify-center">
                <RadioGroup.Root aria-label="Star rating" className="flex" value={selectedStar.toString()}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <RadioGroup.Item
                      aria-label={`${star} ${star === 1 ? 'star' : 'stars'}, ${RATING_WORDS[star]}`}
                      className="star"
                      id={`star-${star}`}
                      key={star}
                      onClick={() => setSelectedStar(star)}
                      onMouseEnter={() => setHoverStar(star)}
                      onMouseLeave={() => setHoverStar(0)}
                      onTouchEnd={() => setHoverStar(0)}
                      onTouchStart={() => setHoverStar(star)}
                      value={star.toString()}
                    >
                      <UiStarIcon hoverStar={hoverStar >= star} selectedStar={selectedStar >= star} />
                    </RadioGroup.Item>
                  ))}
                </RadioGroup.Root>
              </div>
              <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
                {selectedStar === 0
                  ? 'Tap a star — it stays between you and us.'
                  : RATING_WORDS[selectedStar]}
              </p>
              <div className="mt-5 flex flex-col gap-2">
                <Button disabled={selectedStar === 0} flavor="primary" isFullWidth onClick={handleSend} size="large">
                  Send
                </Button>
                <Dialog.Close asChild>
                  <Button flavor="regular" isFullWidth ref={dismissRef} size="large" taste="boundless">
                    Skip
                  </Button>
                </Dialog.Close>
              </div>
            </>
          )}

          {(isHost || hasSent) && (
            <>
              {!isHost && selectedStar > 3 && reviewUrls.length > 0 && (
                <div className="mt-4 flex flex-col rounded-lg bg-gray-100 p-5 text-center dark:bg-gray-700">
                  <div className="font-heading text-base font-bold leading-6">
                    Would you like to share a review?
                  </div>
                  {reviewUrls.map((url) => <ButtonReview key={url} url={url} />)}
                </div>
              )}
              {/* Every rating gets a way out. The exit used to live inside the
                  review block, which only renders above three stars, so a
                  customer who rated the call poorly was left with no labelled
                  way to close it. */}
              <div className="mt-5">
                <Dialog.Close asChild>
                  <Button flavor="primary" isFullWidth ref={dismissRef} size="large">
                    Done
                  </Button>
                </Dialog.Close>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
